import { CenterLine, Point2 } from './centerline';
import { FeatureDef, featureLength } from './features';
import { getTerrain, TERRAIN_CLASSES, TerrainClass, TerrainGrid } from './dem';
import { CHUNK_SIZE } from './grid';
import { valueNoise } from './noise';
import { Surface, SURFACES } from './terrain';

/** Gaussian mound, radius = sigma; with `length` a ridge (the mound swept along the axis at `heading`, 0 = N-S). */
export interface Hill { x: number; z: number; radius: number; height: number; length?: number; heading?: number }
/**
 * Flattened area: a disc, or with `length` a capsule (the disc swept `length` metres along the axis at `heading`,
 * 0 = north-south, centred on (x, z)). `height` is absolute (terrain grids put height 0 at the level origin).
 */
export interface Pad { x: number; z: number; radius: number; height: number; surface: Surface; length?: number; heading?: number }
export interface SurfaceSpan { from: number; to: number; surface: Surface }
export interface TrackDef {
  points: Point2[];
  width: number;
  blend: number; // metres from track edge to untouched ground
  surface: Surface;
  spans: SurfaceSpan[];
  features: FeatureDef[];
  /** Worn-in ruts along the riding line (not on jumps). */
  grooves?: Grooves;
  /** Width per section: [arc length, width] pairs, linear between them round the loop (default: `width` everywhere). */
  widths?: [number, number][];
}
/** Tyre ruts: grooves `depth` deep, gaussian `width` wide, at lateral `offsets` from the line (+ = left). */
export interface Grooves { offsets: number[]; depth: number; width: number }
/** Height of the grooves at lateral offset d. */
export function grooveHeight(g: Grooves, d: number): number {
  let h = 0;
  for (const o of g.offsets) h -= g.depth * Math.exp(-(((d - o) / g.width) ** 2));
  return h;
}
/**
 * Closed riding line beside the track (a training loop): paints the ground, no trees. Optional tyre grooves along
 * it and a raised `lip` (m) at both edges, where the dirt is pushed out.
 */
export interface PathDef { points: Point2[]; width: number; surface: Surface; grooves?: Grooves; lip?: number }
/** Real ground from a terrain grid (src/sim/dem.ts): heights, surface per ground class and tree density per class. */
export interface TerrainDef { name: string; surfaces: Record<TerrainClass, Surface>; trees: Partial<Record<TerrainClass, number>> }
export interface ForestDef { corridor: number; spacing: number; density: number; minRadius: number; maxRadius: number }
export interface Placement { x: number; z: number; heading: number }
export type StartDef = Placement | { s: number };
export type PropDef = { kind: 'cone' | 'pole'; x: number; z: number } | { kind: 'line'; a: Point2; b: Point2 }
  | { kind: 'hay'; x: number; z: number; heading: number }; // straw bale, long side across `heading`

export interface LevelDef {
  id: string;
  name: string;
  seed: number;
  ground: { amplitude: number; scale: number; surface: Surface };
  hills: Hill[];
  pads: Pad[];
  track?: TrackDef;
  forest?: ForestDef;
  terrain?: TerrainDef;
  paths?: PathDef[];
  /** Level maps draw the drill props instead of the track line (the paddock drills share the track's world). */
  map?: 'drills';
  /** Track furniture drawn but not shown on level maps (start gate, straw bales). */
  scenery?: PropDef[];
  /** Red/white course tape on stakes along both lap edges (default: tracks without a forest). */
  courseTape?: boolean;
  /** Where the paddock (van, bikes on stands, coach) stands, facing the riding area. */
  paddock?: Placement;
  starts: Record<string, StartDef>; // 'default' required
  checkpoints: number[];            // arc lengths along the track
  props: PropDef[];
}

export interface Checkpoint extends Placement { s: number; halfWidth: number }

export const PAD_BLEND = 15;
const FEATURE_GAP = 3;
export const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Distance from the segment of `length` centred on (cx, cz) along `heading` (0 = north-south). */
function axisDistance(cx: number, cz: number, length: number, heading: number, x: number, z: number): number {
  const dx = x - cx, dz = z - cz;
  const along = dx * Math.sin(heading) + dz * Math.cos(heading), side = dx * Math.cos(heading) - dz * Math.sin(heading);
  return Math.hypot(side, Math.max(0, Math.abs(along) - length / 2));
}

/** Distance from a pad's axis: its centre, or the axis segment of a capsule pad. */
export function padDistance(pad: Pad, x: number, z: number): number {
  return axisDistance(pad.x, pad.z, pad.length ?? 0, pad.heading ?? 0, x, z);
}

/** A validated level with per-sample lookups for chunk generation. */
export class Level {
  readonly line: CenterLine | undefined;
  /** Widest half width of the lap (for search radii and conservative checks). */
  readonly halfWidth: number;
  /** Half width per centreline sample (sections can differ). */
  readonly halfWidthAt: Float32Array;
  readonly blend: number;
  /** Distance from the centreline inside which chunk generation needs the distance field. */
  readonly influence: number;
  readonly features: readonly FeatureDef[];
  readonly centreHeight: Float32Array;
  readonly featureAt: Int16Array;
  readonly surfaceAt: Uint8Array;
  readonly outer: Int8Array;
  readonly starts: Record<string, Placement>;
  readonly checkpoints: Checkpoint[];
  readonly grid: TerrainGrid | undefined;
  readonly paths: { line: CenterLine; half: number; surface: number; def: PathDef }[];
  /** Surface id per terrain class (index into TERRAIN_CLASSES). */
  readonly classSurface: Uint8Array;
  private readonly classTrees: Float32Array;

  constructor(readonly def: LevelDef) {
    const track = def.track;
    this.grid = def.terrain ? getTerrain(def.terrain.name) : undefined;
    this.paths = (def.paths ?? []).map((p) => ({ line: new CenterLine(p.points), half: p.width / 2, surface: SURFACES.indexOf(p.surface), def: p }));
    this.classSurface = Uint8Array.from(TERRAIN_CLASSES, (c) => SURFACES.indexOf(def.terrain?.surfaces[c] ?? def.ground.surface));
    this.classTrees = Float32Array.from(TERRAIN_CLASSES, (c) => def.terrain?.trees[c] ?? 0);
    this.line = track ? new CenterLine(track.points) : undefined;
    this.halfWidthAt = new Float32Array(this.line?.count ?? 0).fill(track ? track.width / 2 : 0);
    if (this.line && track?.widths?.length) {
      const w = [...track.widths].sort((a, b) => a[0] - b[0]), L = this.line.length, line = this.line, m = w.length;
      for (let i = 0; i < line.count; i++) {
        const s = i * line.step;
        // Neighbouring table entries round the loop: last entry before s and first after it.
        const k = w.findIndex(([ws]) => ws > s);
        const next = k < 0 ? { s: w[0]![0] + L, w: w[0]![1] } : { s: w[k]![0], w: w[k]![1] };
        const pk = k < 0 ? m - 1 : k - 1;
        const prev = pk < 0 ? { s: w[m - 1]![0] - L, w: w[m - 1]![1] } : { s: w[pk]![0], w: w[pk]![1] };
        this.halfWidthAt[i] = (prev.w + ((next.w - prev.w) * (s - prev.s)) / Math.max(1e-6, next.s - prev.s)) / 2;
      }
    }
    this.halfWidth = this.halfWidthAt.reduce((a, v) => Math.max(a, v), 0);
    this.blend = track ? track.blend : 0;
    this.influence = track ? Math.max(this.halfWidth + this.blend, def.forest ? def.forest.corridor + 1 : 0) : 0;
    this.features = track ? [...track.features].sort((a, b) => a.s - b.s) : [];
    const n = this.line?.count ?? 0;
    this.centreHeight = new Float32Array(n);
    this.featureAt = new Int16Array(n).fill(-1);
    this.surfaceAt = new Uint8Array(n);
    this.outer = new Int8Array(this.features.length);
    if (def.forest && CHUNK_SIZE % def.forest.spacing !== 0) throw new Error('forest spacing must divide the chunk size');

    if (this.line && track) {
      const line = this.line;
      const L = line.length;
      for (let i = 0; i < n; i++) {
        this.centreHeight[i] = this.ground(line.xs[i]!, line.zs[i]!);
        this.surfaceAt[i] = SURFACES.indexOf(track.surface);
      }
      for (const span of track.spans) {
        for (let i = 0; i < n; i++) {
          const s = i * line.step;
          if (s >= span.from && s < span.to) this.surfaceAt[i] = SURFACES.indexOf(span.surface);
        }
      }
      this.features.forEach((f, fi) => {
        const len = featureLength(f);
        if (f.s < 0 || f.s + len > L) throw new Error(`feature ${f.kind} at ${f.s} leaves the track`);
        const next = this.features[fi + 1];
        if (next && f.s + len + FEATURE_GAP > next.s) throw new Error(`features overlap at ${next.s}`);
        const i0 = Math.floor(f.s / line.step) - 1;
        const i1 = Math.ceil((f.s + len) / line.step) + 1;
        for (let i = i0; i <= i1; i++) this.featureAt[((i % n) + n) % n] = fi;
        // Outside of the turn is opposite the turn direction: a left turn (curvature > 0) raises the right (d < 0).
        this.outer[fi] = line.curvatureAt(f.s + len / 2, len / 3) > 0 ? -1 : 1;
      });
    }

    this.starts = {};
    for (const [name, st] of Object.entries(def.starts)) this.starts[name] = this.place(st);
    if (!this.starts.default) throw new Error('level needs a default start');
    this.checkpoints = def.checkpoints.map((s) => {
      if (!this.line || s < 0 || s >= this.line.length) throw new Error(`checkpoint ${s} is off the track`);
      return { ...this.place({ s }), s, halfWidth: this.halfWidthAt[Math.round(s / this.line.step) % this.line.count]! };
    });
  }

  private place(st: StartDef): Placement {
    if (!('s' in st)) return st;
    if (!this.line) throw new Error('arc-length start needs a track');
    const p = this.line.pointAt(st.s);
    const t = this.line.tangentAt(st.s);
    return { x: p.x, z: p.z, heading: Math.atan2(t.x, t.z) };
  }

  /** Untouched ground: real terrain grid (if any) + noise + hills, flattened inside pads. */
  ground(x: number, z: number): number {
    const g = this.def.ground;
    let h = g.amplitude === 0 ? 0 : g.amplitude * (valueNoise(this.def.seed, x / g.scale, z / g.scale)
      + 0.3 * valueNoise(this.def.seed + 1, (4 * x) / g.scale, (4 * z) / g.scale));
    if (this.grid) h += this.grid.heightAt(x, z);
    for (const hill of this.def.hills) {
      const r = hill.length ? axisDistance(hill.x, hill.z, hill.length, hill.heading ?? 0, x, z) : Math.hypot(x - hill.x, z - hill.z);
      h += hill.height * Math.exp(-(r * r) / (2 * hill.radius * hill.radius));
    }
    for (const pad of this.def.pads) {
      const w = 1 - smoothstep(pad.radius, pad.radius + PAD_BLEND, padDistance(pad, x, z));
      h += (pad.height - h) * w;
    }
    return h;
  }

  padSurface(x: number, z: number, pads: readonly Pad[] = this.def.pads): Surface | undefined {
    for (const pad of pads) if (padDistance(pad, x, z) <= pad.radius) return pad.surface;
    return undefined;
  }

  /** Surface id of untouched ground: the ground class under (x, z) on a terrain level, else the level's ground. */
  groundSurfaceId(x: number, z: number): number {
    return this.grid ? this.classSurface[this.grid.classAt(x, z)]! : this.classSurface[0]!;
  }

  /** Tree density (0-1 chance per forest grid cell): per ground class on a terrain level, else the forest's own. */
  treeDensity(x: number, z: number): number {
    if (!this.def.forest) return 0;
    return this.grid ? this.classTrees[this.grid.classAt(x, z)]! : this.def.forest.density;
  }
}

